<template>
  <div class="contract-variables-compact">
    <div class="variables-card">
      <!-- Header Section -->
      <div class="card-header">
        <h3 class="card-title">
          <i class="fas fa-code title-icon"></i>
          Variáveis do Contrato
        </h3>
        <span class="variables-count">{{ contract_variables.length }}</span>
      </div>

      <!-- Content Section -->
      <div class="card-content">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <!-- Empty State -->
          <div
            v-if="contract_variables.length === 0 && !loading"
            class="no-results"
          >
            <i class="fas fa-code"></i>
            <span>Nenhuma variável encontrada</span>
          </div>

          <!-- Variables List -->
          <div v-else class="variables-list">
            <div
              v-for="(contract_variable, index) in contract_variables"
              :key="index"
              class="variable-item"
              @click="copyVariable(contract_variable.variable)"
            >
              <div class="variable-code">
                <code>{{ formatBrace(contract_variable.variable) }}</code>
                <button
                  class="copy-btn"
                  @click.stop="copyVariable(contract_variable.variable)"
                >
                  <i class="fas fa-copy"></i>
                </button>
              </div>
              <div class="variable-desc">
                {{ contract_variable.description }}
              </div>
            </div>
          </div>
        </b-overlay>
      </div>

      <!-- Footer -->
      <div class="card-footer">
        <i class="fas fa-lightbulb"></i>
        <span>Clique para copiar</span>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

export default {
  name: "ContractVariableList",

  data() {
    return {
      contract_variables: [],
      loading: false,
    };
  },

  async mounted() {
    await this.getContractVariables();
  },

  methods: {
    async getContractVariables() {
      try {
        this.loading = true;

        const response = await api.get("/contract/contract-variables");

        const { contract_variables } = response.data;

        this.contract_variables = contract_variables || [];

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
        toast("Erro ao carregar variáveis do contrato", "error");
      }
    },

    formatBrace(valor) {
      return `{${valor}}`;
    },

    async copyVariable(variable) {
      try {
        const formattedVariable = this.formatBrace(variable);

        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(formattedVariable);
          toast(`Variável copiada!`, "success");
        } else {
          // Fallback para navegadores mais antigos
          const textArea = document.createElement("textarea");
          textArea.value = formattedVariable;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          toast(`Variável copiada!`, "success");
        }
      } catch (error) {
        console.error("Erro ao copiar variável:", error);
        toast("Erro ao copiar variável", "error");
      }
    },
  },
};
</script>

<style scoped>
.contract-variables-compact {
  width: 100%;
}

.variables-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* Header */
.card-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  padding: 12px 16px;
  border-bottom: 1px solid #0891b2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0284c7;
  margin-right: 6px;
  font-size: 14px;
}

.variables-count {
  background: rgba(255, 255, 255, 0.9);
  color: #0c4a6e;
  font-weight: 600;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}

/* Content */
.card-content {
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.loading-overlay {
  border-radius: 8px;
  min-height: 100px;
}

/* Empty State */
.no-results {
  text-align: center;
  padding: 20px;
  color: #64748b;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.no-results i {
  font-size: 24px;
  color: #cbd5e1;
}

/* Variables List */
.variables-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.variable-item {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.variable-item:hover {
  background: #f1f5f9;
  border-color: #0f65b0;
  transform: translateY(-1px);
}

.variable-code {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.variable-code code {
  background: #0f65b0;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 12px;
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.copy-btn {
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #64748b;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn:hover {
  background: #0f65b0;
  border-color: #0f65b0;
  color: white;
}

.copy-btn i {
  font-size: 10px;
}

.variable-desc {
  font-size: 12px;
  color: #374151;
  line-height: 1.3;
  padding-left: 4px;
}

/* Footer */
.card-footer {
  background: #f8fafc;
  padding: 8px 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}

.card-footer i {
  color: #f59e0b;
  font-size: 12px;
}

/* Scrollbar */
.card-content::-webkit-scrollbar {
  width: 4px;
}

.card-content::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.card-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.card-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    padding: 10px 12px;
  }

  .card-content {
    padding: 10px;
    max-height: 250px;
  }

  .variable-item {
    padding: 8px;
  }

  .variable-code {
    flex-direction: column;
    gap: 4px;
  }

  .copy-btn {
    align-self: flex-end;
  }

  .card-footer {
    padding: 6px 12px;
    font-size: 10px;
  }
}
</style>